import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useOpenLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { LeaveReviewDialog, LeaveSheetView } from '@/modules/leave/hr-leave-manager';
import { createLeaveHistoryTableColumns } from '../../components/leave-history-table/leave-history-table-columns';
import { LeaveRequestFormDialog } from '../../components/leave-request-form-dialog/leave-request-form-dialog';
import { MyLeaveOverview } from '../../components/my-leave-overview/my-leave-overview';
import {
  useCurrentUserId,
  useGetLeaveRequests,
  useWithdrawLeaveRequest,
} from '../../hooks/use-leave';
import { DEFAULT_MY_LEAVE_REQUESTS_ORDER } from '../../graphql/queries';
import { LeaveRequest, LeaveRequestsPagingResult } from '../../types/leave.types';

export const MyLeavePage = () => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const canRequest = useHasPermission(HRM_PERMISSIONS.LEAVE.REQUEST);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.LEAVE.WITHDRAW);
  const canViewSheet = useHasPermission(HRM_PERMISSIONS.LEAVE.VIEW_SHEET);
  const canViewOverview = useHasPermission(HRM_PERMISSIONS.LEAVE.VIEW_OVERVIEW);
  // Earlier HR terms, as legacy offered them to admin/HR only.
  const canPickTerm = useHasPermission(HRM_PERMISSIONS.LEAVE.VIEW_ALL);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [withdrawTarget, setWithdrawTarget] = useState<LeaveRequest | null>(null);
  const [detailTarget, setDetailTarget] = useState<LeaveRequest | null>(null);

  // A notification's link lists only its request and opens it.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_MY_LEAVE_REQUESTS_ORDER,
    linkedId,
  });

  const filter = { UserId: userId };
  const { data, isLoading, error } = useGetLeaveRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { LeaveRequests?: LeaveRequestsPagingResult })?.LeaveRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  useOpenLinkedRecord(linkedId, items, setDetailTarget);

  const { mutateAsync: withdrawLeaveRequest, isPending: isWithdrawing } = useWithdrawLeaveRequest();

  // A refusal (not pending anymore, not the caller's own request) comes back as `IsSuccessful: false`
  // with its own message, toasted by `useWithdrawLeaveRequest`; the confirmation stays open then.
  const handleWithdrawConfirm = async () => {
    if (!withdrawTarget) return;
    const result = await withdrawLeaveRequest(withdrawTarget.ItemId);
    if (result.IsSuccessful) setWithdrawTarget(null);
  };

  const columns = createLeaveHistoryTableColumns({ t, onWithdraw: setWithdrawTarget, canWithdraw });

  const requestsTable = (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={setDetailTarget}
        mobileView="cards"
      />
    </div>
  );

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('MY_LEAVE')}
        actions={
          canRequest
            ? [
                {
                  label: t('REQUEST_LEAVE'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setIsFormOpen(true),
                },
              ]
            : []
        }
      />

      {canViewSheet || canViewOverview ? (
        <Tabs defaultValue="requests">
          {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
          <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
            <TabsTrigger value="requests" className="max-sm:flex-1">
              {t('LEAVE_HISTORY')}
            </TabsTrigger>
            {canViewSheet && (
              <TabsTrigger value="leave-sheet" className="max-sm:flex-1">
                {t('LEAVE_SHEET')}
              </TabsTrigger>
            )}
            {canViewOverview && (
              <TabsTrigger value="leave-overview" className="max-sm:flex-1">
                {t('LEAVE_OVERVIEW')}
              </TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="requests">{requestsTable}</TabsContent>
          {canViewSheet && (
            <TabsContent value="leave-sheet">
              <LeaveSheetView userId={userId} canPickTerm={canPickTerm} />
            </TabsContent>
          )}
          {canViewOverview && (
            <TabsContent value="leave-overview">
              <MyLeaveOverview userId={userId} />
            </TabsContent>
          )}
        </Tabs>
      ) : (
        requestsTable
      )}

      <LeaveRequestFormDialog open={isFormOpen} onOpenChange={setIsFormOpen} />

      {/* The review dialog offers no review/cancel/archive actions on the caller's own request. */}
      <LeaveReviewDialog
        open={Boolean(detailTarget)}
        onOpenChange={(open) => !open && setDetailTarget(null)}
        leaveRequest={detailTarget}
      />

      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_LEAVE_REQUEST')}
        description={t('ARE_YOU_SURE_WANT_TO_WITHDRAW')}
        confirmText={isWithdrawing ? t('WITHDRAWING') : t('WITHDRAW')}
        preventAutoClose={isWithdrawing}
        onConfirm={handleWithdrawConfirm}
      />
    </div>
  );
};
